import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Building2, Check, X, Mail, Megaphone, Eye, MousePointer, Coins, RefreshCw } from "lucide-react";

export const AdminAdvertisersPanel = () => {
  const [advs, setAdvs] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, ads] = await Promise.all([
        api.get("/admin/advertisers"),
        api.get("/admin/ads"),
      ]);
      setAdvs(a.data || []);
      setPending((ads.data || []).filter((x) => x.payment_status === "pending"));
    } catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (uid, status) => {
    try {
      await api.patch(`/admin/advertisers/${uid}`, { status });
      toast.success(status === "approved" ? "Annonceur approuvé ✨" : "Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const markPaid = async (adId) => {
    if (!window.confirm("Marquer cette campagne comme payée et l'activer pour 30 jours ?")) return;
    try {
      await api.post(`/admin/campaigns/${adId}/mark-paid`);
      toast.success("Campagne activée 🚀");
      load();
    } catch { toast.error("Erreur"); }
  };

  if (loading) return <div className="text-white/55 text-sm">Chargement…</div>;

  const reqPending = advs.filter((a) => a.advertiser_status === "pending");
  const approved   = advs.filter((a) => a.advertiser_status === "approved");

  return (
    <div className="space-y-6" data-testid="admin-advertisers-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-black inline-flex items-center gap-2"><Building2 size={20} /> Annonceurs self-service</h2>
          <p className="text-xs text-white/55">Approuve les comptes annonceurs et valide les paiements de leurs campagnes.</p>
        </div>
        <button onClick={load} className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1"><RefreshCw size={12} /> Recharger</button>
      </div>

      {/* CAMPAGNES EN ATTENTE DE PAIEMENT */}
      <Section title="Campagnes en attente de paiement" count={pending.length} accent="brand-orange">
        {pending.length === 0 ? <Empty msg="Aucune campagne en attente." /> : (
          <div className="space-y-2">
            {pending.map((c) => (
              <div key={c.ad_id} className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4 grid md:grid-cols-[1fr_auto] gap-3 items-center" data-testid={`pending-campaign-${c.ad_id}`}>
                <div className="flex gap-3 min-w-0">
                  {c.image_url && <img src={c.image_url} alt="" className="w-16 h-12 object-cover rounded shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{c.title}</p>
                    <p className="text-[11px] text-white/55">
                      <strong className="text-brand-yellow">{Number(c.payment_amount_ar || 0).toLocaleString("fr-FR")} Ar</strong> · {c.pack_key} · {c.placement} · {c.payment_method}
                    </p>
                    <p className="text-[10px] text-white/40 truncate">{c.click_url}</p>
                  </div>
                </div>
                <button onClick={() => markPaid(c.ad_id)} className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-1" data-testid={`mark-paid-${c.ad_id}`}>
                  <Check size={12} /> Marquer payée & activer
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* DEMANDES DE COMPTE EN ATTENTE */}
      <Section title="Demandes de compte en attente" count={reqPending.length} accent="brand-yellow">
        {reqPending.length === 0 ? <Empty msg="Aucune demande." /> : (
          <div className="space-y-2">
            {reqPending.map((u) => (
              <div key={u.user_id} className="rounded-xl border border-brand-yellow/30 bg-brand-yellow/5 p-3 flex items-center gap-3 flex-wrap" data-testid={`pending-adv-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{u.advertiser_company || u.name}</p>
                  <p className="text-[11px] text-white/55">{u.email}</p>
                </div>
                <a href={`mailto:${u.email}`} className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1"><Mail size={12} /> Email</a>
                <button onClick={() => setStatus(u.user_id, "approved")} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 inline-flex items-center gap-1" data-testid={`approve-${u.user_id}`}>
                  <Check size={12} /> Approuver
                </button>
                <button onClick={() => setStatus(u.user_id, "rejected")} className="text-xs px-3 py-1.5 rounded-lg bg-brand-red/15 text-brand-red border border-brand-red/30 hover:bg-brand-red/25 inline-flex items-center gap-1" data-testid={`reject-${u.user_id}`}>
                  <X size={12} /> Rejeter
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ANNONCEURS APPROUVÉS */}
      <Section title="Annonceurs approuvés" count={approved.length} accent="emerald">
        {approved.length === 0 ? <Empty msg="Aucun annonceur approuvé." /> : (
          <div className="grid md:grid-cols-2 gap-2">
            {approved.map((u) => (
              <div key={u.user_id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`approved-adv-${u.user_id}`}>
                <p className="font-bold text-sm">{u.advertiser_company || u.name}</p>
                <p className="text-[11px] text-white/55">{u.email}</p>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

const Section = ({ title, count, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-widest text-white/55 font-bold mb-2">{title} ({count})</p>
    {children}
  </div>
);

const Empty = ({ msg }) => <p className="text-xs text-white/40 italic">{msg}</p>;

export default AdminAdvertisersPanel;
